import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieContext = createContext();

export const useCookies = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookies must be used within a CookieProvider');
  }
  return context;
};

export const CookieProvider = ({ children }) => {
  const [cookieConsent, setCookieConsent] = useState(null); // null = not decided, true = accepted, false = rejected
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    statistics: false,
    marketing: false,
    external: false
  });

  // Load cookie preferences from localStorage on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem('sisens_cookie_consent');
    const savedPreferences = localStorage.getItem('sisens_cookie_preferences');
    
    if (savedConsent !== null) {
      setCookieConsent(JSON.parse(savedConsent));
      if (savedPreferences) {
        setCookiePreferences(JSON.parse(savedPreferences));
      }
    } else {
      // First visit - show cookie banner
      setShowCookieBanner(true);
    }
  }, []);

  const acceptAllCookies = () => {
    const allAccepted = {
      necessary: true,
      statistics: true,
      marketing: true,
      external: true
    };
    
    setCookieConsent(true);
    setCookiePreferences(allAccepted);
    setShowCookieBanner(false);
    
    localStorage.setItem('sisens_cookie_consent', JSON.stringify(true));
    localStorage.setItem('sisens_cookie_preferences', JSON.stringify(allAccepted));
    
    // Enable all cookies/tracking
    enableCookies(allAccepted);
  };

  const rejectAllCookies = () => {
    const onlyNecessary = {
      necessary: true,
      statistics: false,
      marketing: false,
      external: false
    };
    
    setCookieConsent(false);
    setCookiePreferences(onlyNecessary);
    setShowCookieBanner(false);
    
    localStorage.setItem('sisens_cookie_consent', JSON.stringify(false));
    localStorage.setItem('sisens_cookie_preferences', JSON.stringify(onlyNecessary));
    
    // Disable all non-necessary cookies
    enableCookies(onlyNecessary);
  };

  const saveCustomPreferences = (preferences) => {
    const hasAnyOptional = preferences.statistics || preferences.marketing || preferences.external;
    
    setCookieConsent(hasAnyOptional);
    setCookiePreferences(preferences);
    setShowCookieBanner(false);
    
    localStorage.setItem('sisens_cookie_consent', JSON.stringify(hasAnyOptional));
    localStorage.setItem('sisens_cookie_preferences', JSON.stringify(preferences));
    
    enableCookies(preferences);
  };

  const enableCookies = (preferences) => {
    // Here you would enable/disable actual cookies based on preferences
    console.log('Enabling cookies based on preferences:', preferences);
    
    // Example: Google Analytics
    if (preferences.statistics) {
      // Enable Google Analytics or other statistics cookies
      console.log('Statistics cookies enabled');
    } else {
      // Disable statistics cookies
      console.log('Statistics cookies disabled');
    }
    
    // Example: Marketing cookies
    if (preferences.marketing) {
      console.log('Marketing cookies enabled');
    } else {
      console.log('Marketing cookies disabled');
    }
    
    // Example: External cookies (YouTube, social media, etc.)
    if (preferences.external) {
      console.log('External cookies enabled');
    } else {
      console.log('External cookies disabled');
    }
  };

  const showCookieSettings = () => {
    setShowCookieBanner(true);
  };

  const value = {
    cookieConsent,
    cookiePreferences,
    showCookieBanner,
    setShowCookieBanner,
    acceptAllCookies,
    rejectAllCookies,
    saveCustomPreferences,
    showCookieSettings,
    setCookiePreferences
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};

export default CookieContext;